import React, { useEffect, useMemo, useState, ReactNode } from "react";
import { useFilterContext } from "../../../state/FilterContext";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { services } from "../../../globals";
import { MetadataRequest } from "../../../shared/models/metadata-request.model";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface SystemPropertyValuesQueryProps {
    propertyNameArg?: string | null,
    children?: ReactNode,
}

/***
 * Array source that returns the value of a system property.
 */
export const SystemPropertyValuesQuery = React.memo(function SystemPropertyValuesQuery({ propertyNameArg, children }: SystemPropertyValuesQueryProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const { filterState } = useFilterContext();

    // Get property name from the property child
    const [propertyName, setPropertyName] = useState("");
    const propertyChild = useMemo(() => propertyNameArg ? null : findFirstComponentOfType(children, "arg-property"), [children, propertyNameArg]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            const usedPropertyName = propertyNameArg || propertyName;
            if (siteState.site?.name && usedPropertyName) {
                const request: MetadataRequest = { ...filterState };
                // Remove the system property from the request to avoid it being applied to the search
                request.systemProperties = { ...filterState.systemProperties };
                delete request.systemProperties?.[usedPropertyName];
                const valueItems: Array<SourceItem> = await services.search.getPropertyValueSet(
                    siteState.site?.name || "",
                    request,
                    usedPropertyName,
                    false,
                    true,
                );
                const result = valueItems.map((item, ix) => {
                    return { ...item, position: ix + 1 };
                });
                if (isMounted) {
                    setArray(result);
                }
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [siteState, filterState, propertyName, propertyNameArg, setArray]);

    return <>
        {propertyChild && (
            <TextExtractor onExtractedText={setPropertyName}>
                {propertyChild?.props.children}
            </TextExtractor>
        )}
    </>;
});